import {
  annualCostForAddOn,
  annualCostForPlan,
  midpoint,
  type AddOn,
  type AppPricing,
  type BillingCycle,
  type CostRange,
  type Plan,
} from './models.ts';
import type { NeedId } from './needs.ts';

export type CalculatorInput = {
  parents: 1 | 2;
  onlyIPay: boolean;
  billing: BillingCycle;
  needs: NeedId[];
  years: 1 | 2 | 3 | 4 | 5;
};

export type YearCost = {
  year: number;
  low: number;
  high: number;
};

export type AppCostResult = {
  app: AppPricing;
  covered: boolean;
  quoteRequired: boolean;
  plan: Plan | null;
  addOns: AddOn[];
  annual: CostRange | null;
  cumulative: YearCost[];
};

export type CostSummary = {
  results: AppCostResult[];
  harbor: AppCostResult | null;
  cheapestCovering: AppCostResult | null;
  cheapestCompetitor: AppCostResult | null;
  harborDelta: number | null;
  anyCovered: boolean;
};

export function payingParents(input: CalculatorInput): number {
  return input.onlyIPay ? 1 : input.parents;
}

function coversNeeds(plan: Plan, needs: NeedId[]): boolean {
  return needs.every((need) => plan.includedNeeds.includes(need));
}

function bestAddOnCombination(
  missing: NeedId[],
  addOns: AddOn[]
): { addOns: AddOn[]; cost: CostRange } | null {
  const priced = addOns.filter((addOn) => annualCostForAddOn(addOn) !== null);
  let best: { addOns: AddOn[]; cost: CostRange } | null = null;
  const total = 1 << priced.length;
  for (let mask = 0; mask < total; mask += 1) {
    const selected: AddOn[] = [];
    for (let index = 0; index < priced.length; index += 1) {
      if (mask & (1 << index)) selected.push(priced[index]);
    }
    const covered = new Set<NeedId>();
    for (const addOn of selected) {
      for (const need of addOn.covers) covered.add(need);
    }
    if (!missing.every((need) => covered.has(need))) continue;
    const cost = selected.reduce<CostRange>(
      (acc, addOn) => {
        const range = annualCostForAddOn(addOn);
        if (!range) return acc;
        return { low: acc.low + range.low, high: acc.high + range.high };
      },
      { low: 0, high: 0 }
    );
    if (!best || midpoint(cost) < midpoint(best.cost)) {
      best = { addOns: selected, cost };
    }
  }
  return best;
}

function planCandidate(
  plan: Plan,
  needs: NeedId[],
  payers: number,
  billing: BillingCycle
): {
  plan: Plan;
  addOns: AddOn[];
  cost: CostRange;
} | null {
  const planCost = annualCostForPlan(plan, payers, billing);
  if (!planCost) return null;

  if (coversNeeds(plan, needs)) {
    return { plan, addOns: [], cost: planCost };
  }

  const missing = needs.filter((need) => !plan.includedNeeds.includes(need));
  const addOnChoice = bestAddOnCombination(missing, plan.addOns);
  if (!addOnChoice) return null;

  return {
    plan,
    addOns: addOnChoice.addOns,
    cost: {
      low: planCost.low + addOnChoice.cost.low,
      high: planCost.high + addOnChoice.cost.high,
    },
  };
}

function couldNeedQuote(plan: Plan, needs: NeedId[]): boolean {
  const missing = needs.filter((need) => !plan.includedNeeds.includes(need));
  if (missing.length === 0) return false;
  return plan.addOns.some(
    (addOn) => addOn.annual === null && missing.every((need) => addOn.covers.includes(need))
  );
}

export function computeAppCost(app: AppPricing, input: CalculatorInput): AppCostResult {
  const payers = payingParents(input);
  const candidates = app.plans
    .map((plan) => planCandidate(plan, input.needs, payers, input.billing))
    .filter(
      (
        candidate
      ): candidate is { plan: Plan; addOns: AddOn[]; cost: CostRange } =>
        candidate !== null
    );

  if (candidates.length === 0) {
    return {
      app,
      covered: false,
      quoteRequired: app.plans.some((plan) => couldNeedQuote(plan, input.needs)),
      plan: null,
      addOns: [],
      annual: null,
      cumulative: [],
    };
  }

  const selected = candidates.reduce((mostExpensive, candidate) =>
    midpoint(candidate.cost) > midpoint(mostExpensive.cost)
      ? candidate
      : mostExpensive
  );

  return {
    app,
    covered: true,
    quoteRequired: false,
    plan: selected.plan,
    addOns: selected.addOns,
    annual: selected.cost,
    cumulative: Array.from({ length: 5 }, (_, index) => ({
      year: index + 1,
      low: selected.cost.low * (index + 1),
      high: selected.cost.high * (index + 1),
    })),
  };
}

export function computeAllCosts(
  apps: AppPricing[],
  input: CalculatorInput
): AppCostResult[] {
  return apps.map((app) => computeAppCost(app, input));
}

export function totalForYears(result: AppCostResult, years: number): CostRange | null {
  if (!result.annual) return null;
  return { low: result.annual.low * years, high: result.annual.high * years };
}

export function summarizeCosts(
  results: AppCostResult[],
  input: CalculatorInput
): CostSummary {
  const covering = results.filter((result) => result.covered && result.annual !== null);
  const harbor = results.find((result) => result.app.slug === 'harbor') ?? null;
  const cheapestCovering =
    covering.length === 0
      ? null
      : covering.reduce((cheapest, result) =>
          midpoint(result.annual as CostRange) < midpoint(cheapest.annual as CostRange)
            ? result
            : cheapest
        );
  const competitors = covering.filter((result) => result.app.slug !== 'harbor');
  const cheapestCompetitor =
    competitors.length === 0
      ? null
      : competitors.reduce((cheapest, result) =>
          midpoint(result.annual as CostRange) < midpoint(cheapest.annual as CostRange)
            ? result
            : cheapest
        );

  let harborDelta: number | null = null;
  if (harbor?.annual && cheapestCompetitor?.annual) {
    harborDelta =
      (midpoint(harbor.annual) - midpoint(cheapestCompetitor.annual)) * input.years;
  }

  return {
    results,
    harbor,
    cheapestCovering,
    cheapestCompetitor,
    harborDelta,
    anyCovered: covering.length > 0,
  };
}
